/**
 * Login Page
 * Handles login, registration, and tenant selection
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useTenant } from '../hooks/useTenant';

type AuthMode = 'login' | 'register' | 'select_tenant';

interface TenantOption {
  id: number;
  slug: string;
  name: string;
  role: string;
}

const Login: React.FC = () => {
  const { login, register, switchTenant, isLoading, tenants } = useAuth();
  const { branding } = useTenant();

  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [pendingTenants, setPendingTenants] = useState<TenantOption[]>([]);

  // Prefill email if the user chose "Remember me" previously
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('remembered_email');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  // Test accounts for development
  const testAccounts = [
    { email: 'owner@demo.test', password: 'owner123', role: 'Owner', color: '#8b5cf6' },
    { email: 'admin@demo.test', password: 'admin123', role: 'Admin', color: '#3b82f6' },
    { email: 'manager@demo.test', password: 'manager123', role: 'Manager', color: '#10b981' },
    { email: 'drafter@demo.test', password: 'drafter123', role: 'Drafter', color: '#f59e0b' },
    { email: 'client@demo.test', password: 'client123', role: 'Client', color: '#ec4899' },
  ];

  const fillTestAccount = (account: typeof testAccounts[0]) => {
    setEmail(account.email);
    setPassword(account.password);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    try {
      await login(email, password);
      if (rememberMe) {
        localStorage.setItem('remembered_email', email);
      } else {
        localStorage.removeItem('remembered_email');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    try {
      await register({
        email,
        password,
        name,
        company_name: companyName
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    }
  };

  const handleTenantSelect = async (tenantId: number) => {
    try {
      await switchTenant(tenantId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to select tenant');
    }
  };

  // Show tenant selection if user has multiple tenants
  if (mode === 'select_tenant' && pendingTenants.length > 0) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <img src={branding.logo_url} alt={branding.company_name} className="login-logo" />
            <h1>Select Workspace</h1>
            <p>Choose which workspace to open</p>
          </div>
          
          <div className="tenant-list">
            {pendingTenants.map(tenant => (
              <button
                key={tenant.id}
                className="tenant-option"
                onClick={() => handleTenantSelect(tenant.id)}
              >
                <div className="tenant-avatar">
                  {tenant.name.charAt(0).toUpperCase()}
                </div>
                <div className="tenant-info">
                  <span className="tenant-name">{tenant.name}</span>
                  <span className="tenant-role">{tenant.role}</span>
                </div>
                <span className="tenant-arrow">→</span>
              </button>
            ))}
          </div>
          
          <button className="btn-text" onClick={() => setMode('login')}>
            ← Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Header */}
        <div className="login-header">
          <img 
            src={branding.logo_url} 
            alt={branding.company_name} 
            className="login-logo"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/static/logo-default.svg';
            }}
          />
          <h1>{mode === 'login' ? 'Welcome back' : 'Create account'}</h1>
          <p>
            {mode === 'login' 
              ? 'Sign in to access your workspace'
              : 'Start your 14-day free trial'
            }
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Login Form */}
        {mode === 'login' && (
          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoFocus
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <div className="form-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                Remember me
              </label>
              <a href="/forgot-password" className="forgot-link">
                Forgot password?
              </a>
            </div>

            <button
              type="submit"
              className="btn-primary full-width"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>

            {/* Test Accounts */}
            <div className="test-accounts">
              <div className="test-accounts-header">Quick Login (Dev)</div>
              <div className="test-accounts-grid">
                {testAccounts.map(account => (
                  <button
                    key={account.email}
                    type="button"
                    className="test-account-btn"
                    onClick={() => fillTestAccount(account)}
                    style={{ borderColor: account.color }}
                  >
                    <span className="test-dot" style={{ background: account.color }}></span>
                    {account.role}
                  </button>
                ))}
              </div>
            </div>
          </form>
        )}

        {/* Register Form */}
        {mode === 'register' && (
          <form onSubmit={handleRegister} className="login-form">
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Smith"
                required
                autoFocus
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="company">Company Name</label>
              <input
                id="company"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Acme Roofing Co."
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="reg-email">Email</label>
              <input
                id="reg-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="reg-password">Password</label>
              <input
                id="reg-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                minLength={8}
                required
              />
            </div>

            <button 
              type="submit" 
              className="btn-primary full-width"
              disabled={isLoading}
            >
              {isLoading ? 'Creating account...' : 'Create account'}
            </button>
            
            <p className="terms-text">
              By creating an account, you agree to our{' '}
              <a href="/terms">Terms of Service</a> and{' '}
              <a href="/privacy">Privacy Policy</a>.
            </p>
          </form>
        )}

        {/* Mode Toggle */}
        <div className="mode-toggle">
          {mode === 'login' ? (
            <>
              Don't have an account?{' '}
              <button className="btn-text" onClick={() => setMode('register')}>
                Create one
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button className="btn-text" onClick={() => setMode('login')}>
                Sign in
              </button>
            </>
          )}
        </div>

        {/* Powered By */}
        {branding.show_powered_by && (
          <div className="powered-by">
            Powered by <a href="https://shopdrawings.ai">ShopDrawings.AI</a>
          </div>
        )}
      </div>

      {/* Background Decoration */}
      <div className="login-background">
        <div className="bg-gradient"></div>
        <div className="bg-pattern"></div>
      </div>

      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          position: relative;
          overflow: hidden;
        }

        .login-container {
          width: 100%;
          max-width: 420px;
          background: var(--bg-secondary);
          border-radius: 16px;
          padding: 40px;
          position: relative;
          z-index: 1;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        }

        .login-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .login-logo {
          height: 48px;
          margin-bottom: 24px;
        }

        .login-header h1 {
          font-size: 24px;
          margin-bottom: 8px;
          color: var(--text-primary);
        }

        .login-header p {
          color: var(--text-secondary);
          font-size: 14px;
        }

        .error-message {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid var(--danger);
          color: var(--danger);
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 14px;
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-group label {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-secondary);
        }

        .form-group input {
          width: 100%;
          padding: 12px 16px;
          background: var(--bg-primary);
          border: 1px solid var(--border-color, #334155);
          border-radius: 8px;
          color: var(--text-primary);
          font-size: 16px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .form-group input:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2);
        }

        .form-group input::placeholder {
          color: var(--text-secondary);
          opacity: 0.6;
        }

        .form-options {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 14px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--text-secondary);
          cursor: pointer;
        }

        .checkbox-label input {
          width: 16px;
          height: 16px;
          accent-color: var(--primary);
        }

        .forgot-link {
          color: var(--primary);
          text-decoration: none;
        }

        .forgot-link:hover {
          text-decoration: underline;
        }

        .btn-primary {
          padding: 14px 24px;
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-primary:hover {
          background: var(--primary-dark, #1d4ed8);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .full-width {
          width: 100%;
        }

        .terms-text {
          font-size: 12px;
          color: var(--text-secondary);
          text-align: center;
          margin-top: 8px;
        }

        .terms-text a {
          color: var(--primary);
          text-decoration: none;
        }

        .mode-toggle {
          text-align: center;
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid var(--border-color, #334155);
          color: var(--text-secondary);
          font-size: 14px;
        }

        .btn-text {
          background: none;
          border: none;
          color: var(--primary);
          cursor: pointer;
          font-size: inherit;
          padding: 0;
        }

        .btn-text:hover {
          text-decoration: underline;
        }

        .powered-by {
          text-align: center;
          margin-top: 24px;
          font-size: 12px;
          color: var(--text-secondary);
          opacity: 0.6;
        }

        .powered-by a {
          color: inherit;
          text-decoration: none;
        }

        /* Tenant Selection */
        .tenant-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 24px;
        }

        .tenant-option {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: var(--bg-primary);
          border: 1px solid var(--border-color, #334155);
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s;
          text-align: left;
        }

        .tenant-option:hover {
          border-color: var(--primary);
          background: rgba(37, 99, 235, 0.05);
        }

        .tenant-avatar {
          width: 48px;
          height: 48px;
          background: var(--primary);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 600;
          color: white;
        }

        .tenant-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .tenant-name {
          font-weight: 500;
          color: var(--text-primary);
        }

        .tenant-role {
          font-size: 13px;
          color: var(--text-secondary);
          text-transform: capitalize;
        }

        .tenant-arrow {
          color: var(--text-secondary);
          font-size: 20px;
        }

        /* Background */
        .login-background {
          position: fixed;
          inset: 0;
          z-index: 0;
          pointer-events: none;
        }

        .bg-gradient {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 30% 20%, rgba(37, 99, 235, 0.15), transparent 50%),
                      radial-gradient(circle at 70% 80%, rgba(16, 185, 129, 0.1), transparent 50%);
        }

        .bg-pattern {
          position: absolute;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        }

        /* Test Accounts */
        .test-accounts {
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px dashed var(--border-color, #334155);
        }

        .test-accounts-header {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-secondary);
          text-align: center;
          margin-bottom: 12px;
          opacity: 0.7;
        }

        .test-accounts-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          justify-content: center;
        }

        .test-account-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: var(--bg-primary);
          border: 1px solid;
          border-radius: 6px;
          font-size: 12px;
          color: var(--text-primary);
          cursor: pointer;
          transition: all 0.2s;
        }

        .test-account-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }

        .test-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }

        @media (max-width: 480px) {
          .login-container {
            padding: 24px;
          }
        }
      `}</style>
    </div>
  );
};

export default Login;
